import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Dropdown } from 'react-bootstrap';
import { 
  Person, 
  PersonPlus, 
  PersonGear, 
  PersonCheck, 
  Eye, 
  ShieldFill,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  ArrowClockwise
} from 'react-bootstrap-icons';
import { UserRole, ROLE_PERMISSIONS, getRoleConfig, hasPermission } from '../types/userRoles';
import { useAccount } from '../contexts/AccountContext';
import { useUser } from '../contexts/UserContext';
import { 
  useGetUsersQuery, 
  useCreateUserMutation, 
  useUpdateUserMutation, 
  useDeleteUserMutation,
  useSendInvitationMutation,
  useGetUserPreferencesQuery
} from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../utils/tenantTimezone';
import { useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';

const UserManagement = () => {
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  const { currentUser } = useUser();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'viewer',
    status: 'pending'
  });
  const [alert, setAlert] = useState(null);

  const tenantIdForPage = useTenantIdForPreferences();

  // API hooks — roster scoped to current company tenant (top bar).
  const { data: usersData, isLoading: usersLoading, error: usersError, refetch } = useGetUsersQuery(
    tenantIdForPage ? { tenantId: tenantIdForPage } : undefined
  );
  const { data: userPreferencesData } = useGetUserPreferencesQuery(
    tenantIdForPage ? { tenantId: tenantIdForPage } : undefined
  );
  const tenantTimezone = getTenantTimezoneFromPreferencesData(userPreferencesData);
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [sendInvitation] = useSendInvitationMutation();

  // Extract users from API response
  const users = usersData?.data?.users || [];

  // Show error if API call fails
  useEffect(() => {
    if (usersError) {
      setAlert({ 
        type: 'danger', 
        message: `Failed to load users: ${usersError?.data?.message || usersError?.message || 'Unknown error'}` 
      });
    }
  }, [usersError]);

  const getRoleBadgeVariant = (role) => {
    const roleConfig = getRoleConfig(role);
    return roleConfig?.color || 'secondary';
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'danger';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    return toLocaleDateStringInTimezone(dateString, 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }, tenantTimezone);
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      setAlert({ type: 'danger', message: 'Name and email are required' });
      return;
    }

    try {
      const result = await createUser({
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        sendInvitation: true, // Send invitation email automatically
        ...(tenantIdForPage ? { tenantId: tenantIdForPage } : {}),
      }).unwrap();

      setNewUser({ name: '', email: '', role: 'viewer', status: 'pending' });
      setShowAddModal(false);
      
      if (result.data?.invitationSent) {
        setAlert({ 
          type: 'success', 
          message: `User created successfully! An invitation email has been sent to ${newUser.email}. They can set their password using the link in the email.` 
        });
      } else {
        setAlert({ type: 'success', message: 'User created successfully!' });
      }
    } catch (error) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create user';
      setAlert({ type: 'danger', message: errorMessage });
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      await updateUser({
        userId: selectedUser.id,
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role
      }).unwrap();

      setShowEditModal(false);
      setSelectedUser(null);
      setAlert({ type: 'success', message: 'User updated successfully!' });
    } catch (error) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to update user';
      setAlert({ type: 'danger', message: errorMessage });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id).unwrap();
      
      setShowDeleteModal(false);
      setSelectedUser(null);
      setAlert({ type: 'success', message: 'User deleted successfully!' });
    } catch (error) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete user';
      setAlert({ type: 'danger', message: errorMessage });
    }
  };

  const handleSendInvitation = async (userId) => {
    try {
      await sendInvitation(userId).unwrap();
      setAlert({ type: 'success', message: 'Invitation email sent successfully!' });
    } catch (error) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to send invitation';
      setAlert({ type: 'danger', message: errorMessage });
    }
  };

  const getRoleIcon = (role) => {
    const roleConfig = getRoleConfig(role);
    switch (roleConfig?.icon) {
      case 'ShieldFill': return <ShieldFill className="me-1" />;
      case 'PersonGear': return <PersonGear className="me-1" />;
      case 'PersonCheck': return <PersonCheck className="me-1" />;
      case 'Eye': return <Eye className="me-1" />;
      default: return <Person className="me-1" />;
    }
  };

  if (isLoading || usersLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <ArrowClockwise className="spinning" size={32} />
          <div className="mt-2">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="User Management"
        showHelp
        children={
          <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)} className="d-flex align-items-center">
            <PersonPlus className="me-1" />
            Add User
          </Button>
        }
      />
      <Container fluid className="py-3">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <p className="text-muted mb-0">
                Manage user accounts and permissions
                {!isAllAccountsSelected && selectedAccount && (
                  <Badge bg="primary" className="ms-2">{selectedAccount.alias}</Badge>
                )}
              </p>
            </div>
          </div>

          {alert && (
            <Alert 
              variant={alert.type} 
              dismissible 
              onClose={() => setAlert(null)}
              className="mb-4"
            >
              {alert.message}
            </Alert>
          )}

          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Created</th>
                    <th width="120">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px', fontSize: '14px' }}
                          >
                            {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold">{user.name}</div>
                            <div className="text-muted small">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg={getRoleBadgeVariant(user.role)} className="d-flex align-items-center w-auto">
                          {getRoleIcon(user.role)}
                          {getRoleConfig(user.role)?.name}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusBadgeVariant(user.status)}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="text-muted">
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                      </td>
                      <td className="text-muted">
                        {formatDate(user.createdAt)}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add User Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newUser.name || ''}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={newUser.email || ''}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={newUser.role || 'viewer'}
                    onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                  >
                    {ROLE_PERMISSIONS.map(role => (
                      <option key={role.role} value={role.role}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={newUser.status || 'pending'}
                    onChange={(e) => setNewUser(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddUser}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Add User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedUser.name}
                      onChange={(e) => setSelectedUser(prev => prev ? { ...prev, name: e.target.value } : null)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={selectedUser.email}
                      onChange={(e) => setSelectedUser(prev => prev ? { ...prev, email: e.target.value } : null)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Role</Form.Label>
                    <Form.Select
                      value={selectedUser.role}
                      onChange={(e) => setSelectedUser(prev => prev ? { ...prev, role: e.target.value } : null)}
                    >
                      {ROLE_PERMISSIONS.map(role => (
                        <option key={role.role} value={role.role}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={selectedUser.status}
                      onChange={(e) => setSelectedUser(prev => prev ? { ...prev, status: e.target.value } : null)}
                    >
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditUser}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete User Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <p>Are you sure you want to delete the user <strong>{selectedUser.name}</strong>?</p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteUser}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default UserManagement;
